// hx primitives — the reusable building blocks every /ops page composes from.
//
// Each one is a thin React wrapper over the `hx-*` classes in
// shell-helix-data.css, which read ONLY the tokens in theme.css. So a colour,
// radius or spacing change is made once (theme.css / the class) and every
// button, card, table, chip and input on every page follows. Pages must not
// restyle these with inline colours; pass `className` / `style` for layout
// (widths, margins) only.
//
// Dropdowns (HxSelect, HxMultiSelect), the date range, modal, drawer, KPI
// tiles, charts, toasts and the info tip live in their sibling files and are
// re-exported from ./index.ts with these.

import { forwardRef } from "react";
import type {
  Ref,
  ButtonHTMLAttributes,
  ChangeEvent,
  ComponentProps,
  CSSProperties,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { ShellIcon } from "../shell/icons";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Alert } from "../ui/alert";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../ui/empty";
import { Badge } from "../ui/badge";
import { Card, CardAction, CardHeader, CardTitle } from "../ui/card";
import { Table } from "../ui/table";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { FieldLabel } from "../ui/field";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../lib/utils";

/** The icon set (components/shell/icons.tsx) — one place for every glyph. */
export const HxIcon = ShellIcon;

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// ── Buttons ─────────────────────────────────────────────────────────────────

export type HxButtonVariant = "default" | "primary" | "ghost" | "danger";
export type HxButtonSize = "sm" | "icon";

/** The `hx-btn` class set for an element that is not an HxButton but should
 *  look like the old one (six callers put it on a trigger or link). HxButton
 *  itself renders the template's button since pass 043. `icon` is the square
 *  28px glyph-only button (`sm` + `icon`). */
export function hxButtonClass(variant: HxButtonVariant = "default", size?: HxButtonSize | null, className?: string): string {
  return cx(
    "hx-btn",
    variant !== "default" && variant,
    size === "sm" && "sm",
    size === "icon" && "sm icon",
    className,
  );
}

interface HxButtonBase {
  variant?: HxButtonVariant;
  size?: HxButtonSize;
  /** With `danger`, a solid red fill instead of the template's pale one: for
   *  the action that must be unmistakable (the call bar's End). */
  solid?: boolean;
  /** Glyph name from the icon set, rendered before the label. */
  icon?: string;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}
export type HxButtonProps = HxButtonBase &
  (
    | ({ to: string } & Omit<HTMLAttributes<HTMLAnchorElement>, "className" | "style">)
    | ({ to?: undefined } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "style">)
  );

// HxButton's variants and sizes on the template's button (ui/button.tsx).
const BUTTON_VARIANT = { default: "outline", primary: "default", ghost: "ghost", danger: "destructive" } as const;
const BUTTON_SIZE = { sm: "sm", icon: "icon-sm" } as const;
// The template has no solid destructive button; this raises its fill to the
// full red, with the same tokens the old solid danger read (--destructive is
// --neg's value, --primary-foreground --on-accent's), in both themes.
const SOLID_DANGER = "bg-destructive text-primary-foreground hover:bg-destructive/90 dark:bg-destructive dark:hover:bg-destructive/90";

/** The template's button (ui/button.tsx): outline by default; `primary` is
 *  the filled main action, `ghost` bare, `danger` the template's pale red
 *  (`solid` for a full red). Pass `to` for a router link that looks like a
 *  button. Forwards its ref, so a popover or menu trigger can be an HxButton
 *  too. Keeps the class `hx-btn` as a marker: page rules that restyle every
 *  other button skip it, and the old hx-btn look rules skip a template button
 *  (hx-controls.css). Record: docs/testing/ui-migration/043-hx-button.md. */
export const HxButton = forwardRef<HTMLButtonElement, HxButtonProps>(function HxButton(props, ref) {
  const { variant = "default", size, solid, icon, children, className, style, ...rest } = props;
  const look = {
    variant: BUTTON_VARIANT[variant],
    size: size ? BUTTON_SIZE[size] : ("default" as const),
    // disabled:pointer-events-auto: the template's disabled:pointer-events-none
    // hid a disabled button's `title`, which says why it is disabled (the call
    // bar's Transfer, Complete, Conference); a disabled button still takes no
    // click. As HxInput does (pass 044). Record: 043-hx-button.md, Audit notes.
    className: cn("hx-btn disabled:pointer-events-auto", solid && variant === "danger" && SOLID_DANGER, className),
    style,
  };
  const inner = (
    <>
      {icon && <HxIcon name={icon} size={size ? 13 : 15} />}
      {children}
    </>
  );
  if ("to" in rest && typeof rest.to === "string") {
    const { to, ...anchor } = rest as { to: string } & HTMLAttributes<HTMLAnchorElement>;
    return (
      <Button asChild {...look}>
        <Link ref={ref as unknown as Ref<HTMLAnchorElement>} to={to} {...anchor} data-slot="button">
          {inner}
        </Link>
      </Button>
    );
  }
  const button = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  // data-slot="button" last: a Radix trigger wrapping this (asChild) would
  // otherwise hand it its own data-slot, and the old hx-btn rules, which skip
  // only data-slot="button", would draw it again.
  return (
    <Button ref={ref} type="button" {...look} {...button} data-slot="button">
      {inner}
    </Button>
  );
});

// ── Surfaces ────────────────────────────────────────────────────────────────

export interface HxCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Card heading (rendered as `.card-title`). */
  title?: ReactNode;
  /** Right-aligned caption or control beside the heading. */
  sub?: ReactNode;
}
/** The panel: the template's card (ui/card.tsx), its heading row `CardHeader`
 *  holding `CardTitle` and, for `sub`, `CardAction`. It keeps the classes
 *  `hx-card`, `card-title` and `sub`, which page rules restyle cards through,
 *  and today's layout: a block with the 16px padding the `.hx-card` rule
 *  keeps (a caller's inline padding is the only padding), children in normal
 *  flow outside `CardContent`, nothing clipped, the heading a flex row with
 *  the caption pushed right. Record: docs/testing/ui-migration/057-hx-card.md. */
export function HxCard({ title, sub, children, className, ...rest }: HxCardProps) {
  return (
    <Card className={cn("hx-card block flex-row gap-0 overflow-visible text-[length:inherit] leading-[inherit] text-inherit", className)} {...rest}>
      {(title || sub) && (
        <CardHeader className="card-title rounded-none">
          {(title || title === 0) && <CardTitle className="flex items-center gap-2">{title}</CardTitle>}
          {sub && <CardAction className="sub self-center">{sub}</CardAction>}
        </CardHeader>
      )}
      {children}
    </Card>
  );
}

/** `hx-filterbar` — the toolbar row above a dashboard or table. Push the
 *  trailing status to the right with `style={{ marginLeft: "auto" }}`. */
export function HxFilterBar({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx("hx-filterbar", className)} {...rest}>
      {children}
    </div>
  );
}

/** Two- or three-column panel grid. */
export function HxGrid({ cols = 2, className, children, ...rest }: HTMLAttributes<HTMLDivElement> & { cols?: 2 | 3 }) {
  return (
    <div className={cx(cols === 3 ? "hx-grid-3" : "hx-grid-2", className)} {...rest}>
      {children}
    </div>
  );
}

// ── Table ───────────────────────────────────────────────────────────────────

export interface HxTableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Minimum table width before the wrapper scrolls horizontally (default 760). */
  minWidth?: number;
  /** Scroll height of the wrapper (px or CSS length); the header row stays sticky. */
  maxHeight?: number | string;
  /** Class for the scrolling wrapper. */
  wrapClassName?: string;
}
/** The template's table (ui/table.tsx) inside Helix's own wrap,
 *  `hx-table-wrap`: the frame, the one scroller (the template's container
 *  does not scroll inside it, hx-controls.css), `wrapClassName` and
 *  `maxHeight`, which the template's container takes no props for. The table
 *  keeps the class `hx-table`, whose rules draw the raw `<th>` and `<td>`
 *  callers write and its 760px minimum. Children are the `<thead>` /
 *  `<tbody>` / `<tfoot>`; see HxPager for the footer row. Record:
 *  docs/testing/ui-migration/058-hx-table.md. */
export function HxTable({ minWidth, maxHeight, wrapClassName, className, style, children, ...rest }: HxTableProps) {
  return (
    <div className={cx("hx-table-wrap", wrapClassName)} style={maxHeight != null ? { maxHeight, overflowY: "auto" } : undefined}>
      <Table className={cn("hx-table leading-[inherit]", className)} style={minWidth != null ? { ...style, minWidth } : style} {...rest}>
        {children}
      </Table>
    </div>
  );
}

export interface HxPagerProps {
  total: number;
  offset: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  /** Noun for the count, e.g. "calls" → "128 calls". Default "rows". */
  noun?: string;
  /** Columns spanned when rendered inside a `<tfoot>`. Omit to render standalone. */
  colSpan?: number;
}
/** `hx-pager` — "N rows · Prev / Next". Prev and Next are the template's
 *  pagination buttons (pagination.tsx's PaginationPrevious and
 *  PaginationNext: a ghost Button with a chevron), as buttons, not links, and
 *  named by their words at every width. They keep the marker class `pg`,
 *  which the old pager rules skip. Rendered as a `<tfoot>` row when `colSpan`
 *  is given, otherwise as a plain footer div. Record:
 *  docs/testing/ui-migration/059-hx-pager.md. */
export function HxPager({ total, offset, pageSize, onPrev, onNext, noun = "rows", colSpan }: HxPagerProps) {
  const body = (
    <div className="hx-pager">
      {total} {noun}
      <span className="pages">
        <Button type="button" variant="ghost" className="pg pl-1.5! text-foreground" disabled={offset === 0} onClick={onPrev}>
          <ChevronLeftIcon data-icon="inline-start" />
          Prev
        </Button>
        <Button type="button" variant="ghost" className="pg pr-1.5! text-foreground" disabled={offset + pageSize >= total} onClick={onNext}>
          Next
          <ChevronRightIcon data-icon="inline-end" />
        </Button>
      </span>
    </div>
  );
  if (colSpan == null) return body;
  return (
    <tfoot>
      <tr>
        <td colSpan={colSpan} style={{ padding: 0 }}>{body}</td>
      </tr>
    </tfoot>
  );
}

// ── Atoms ───────────────────────────────────────────────────────────────────

export type HxTone = "neutral" | "ok" | "warn" | "neg" | "high" | "value" | "violet";
const TONE_CLASS: Record<HxTone, string | null> = {
  neutral: null, ok: "tag-ok", warn: "tag-warn", neg: "tag-neg", high: "tag-high", value: "tag-value", violet: "tag-violet",
};
/** Status / category pill in a semantic tone: the template's badge
 *  (ui/badge.tsx), `secondary` for a neutral chip and `default` for violet,
 *  keeping the classes `chip` and `tag-*`, whose rules carry Helix's tone
 *  colours (the template has none). Record:
 *  docs/testing/ui-migration/052-hx-chip.md. */
export function HxChip({ tone = "neutral", className, children, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: HxTone }) {
  return (
    <Badge variant={tone === "violet" ? "default" : "secondary"} className={cn("chip", TONE_CLASS[tone], className)} {...rest}>
      {children}
    </Badge>
  );
}

/** `hx-live` — the "Updated just now" pulse. */
export function HxLive({ children, className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cx("hx-live", className)} {...rest}>
      <span className="dot" />
      {children}
    </span>
  );
}

// ── Form controls ───────────────────────────────────────────────────────────

/** Text input: the template's input (ui/input.tsx), keeping the marker class
 *  `hx-input` (page rules that restyle every other input skip it, and the
 *  layout rules that size it still apply). It keeps the browser's own width,
 *  as before (`w-auto` over the template's `w-full`; a caller's width wins),
 *  and a disabled field still takes the pointer so its title can say why.
 *  For a select use HxSelect / HxMultiSelect. Record:
 *  docs/testing/ui-migration/044-hx-input.md. */
export function HxInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <Input className={cn("hx-input w-auto disabled:pointer-events-auto", className)} {...rest} />;
}

/** Multi-line text: the template's textarea (ui/textarea.tsx), keeping the
 *  marker class `hx-textarea`. Its size is Helix's: `rows` sets the height
 *  (`field-sizing-fixed` over the template's grow-with-content), full width
 *  and inline as before, vertical resize only, and a disabled field still
 *  takes the pointer. Record:
 *  docs/testing/ui-migration/045-hx-textarea.md. */
export function HxTextarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Textarea
      className={cn("hx-textarea inline-block field-sizing-fixed resize-y disabled:pointer-events-auto", className)}
      {...rest}
    />
  );
}

/** `hx-search` — input with the leading magnifier. The field is HxInput (the
 *  template's input); the wrapper, the glyph and the width rules on
 *  `.hx-search .hx-input` stay Helix's. Record:
 *  docs/testing/ui-migration/046-hx-search.md. */
export function HxSearch({ className, style, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span className={cx("hx-search", className)} style={style}>
      <HxIcon name="search" size={14} />
      <HxInput type="search" {...rest} />
    </span>
  );
}

/** A label over a control. The label is the template's FieldLabel
 *  (ui/field.tsx), kept in normal text flow (`block`) so inline marks such as
 *  "(optional)" sit as they did. The wrapper stays Helix's `hx-field` column
 *  (8px gap, the template's), not the template's Field, which would stretch
 *  every control to full width and add an unnamed group per field. Record:
 *  docs/testing/ui-migration/063-hx-field.md. */
export function HxField({ label, children, className, ...rest }: HTMLAttributes<HTMLDivElement> & { label: ReactNode }) {
  return (
    <div className={cx("hx-field", className)} {...rest}>
      <FieldLabel className="block">{label}</FieldLabel>
      {children}
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

export interface HxTab {
  id: string;
  label: ReactNode;
  badge?: ReactNode;
}
/** The template's tabs (ui/tabs.tsx): `variant` "default" is the grey strip
 *  with a white pill, "line" the underline a screen's own tabs take. A click
 *  calls `onChange`, the picked tab included, as it always has: Radix's own
 *  value change is not wired (manual activation), and Enter or Space reach
 *  the same click; arrow keys move focus along the row. No panels, so no
 *  `aria-controls`; the root takes no box (`contents`). The classes `hx-tabs`
 *  and `hx-tab` stay as markers (the old tab rules skip them). Record:
 *  docs/testing/ui-migration/060-hx-tabs.md. */
export function HxTabs({ tabs, active, onChange, className, variant = "default" }: { tabs: HxTab[]; active: string; onChange: (id: string) => void; className?: string; variant?: "default" | "line" }) {
  return (
    <Tabs value={active} activationMode="manual" className="contents">
      <TabsList variant={variant} className={cn("hx-tabs", className)}>
        {tabs.map((t) => (
          <TabsTrigger key={t.id} value={t.id} aria-controls={undefined} className="hx-tab" onClick={() => onChange(t.id)}>
            {t.label}
            {t.badge != null && <span className="nav-badge">{t.badge}</span>}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

/** Empty / loading state: the template's empty state (ui/empty.tsx), a title
 *  over a muted explainer, keeping the marker class `hx-empty` and its
 *  natural size (`w-auto flex-initial` over the template's `w-full flex-1`).
 *  The words are the caller's. Record: docs/testing/ui-migration/050-hx-empty.md. */
export function HxEmpty({ title, children, className, ...rest }: HTMLAttributes<HTMLDivElement> & { title?: ReactNode }) {
  return (
    <Empty className={cn("hx-empty w-auto flex-initial", className)} {...rest}>
      <EmptyHeader>
        {title && <EmptyTitle>{title}</EmptyTitle>}
        {children != null && children !== false && <EmptyDescription>{children}</EmptyDescription>}
      </EmptyHeader>
    </Empty>
  );
}

/** Inline error banner: the template's destructive alert (ui/alert.tsx),
 *  keeping the marker class `hx-alert`, today's row layout (a Retry or
 *  Dismiss button sits beside the text) and its 12px margin below; a
 *  caller's margin wins. Record: docs/testing/ui-migration/048-hx-alert.md. */
export function HxAlert({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <Alert variant="destructive" className={cn("hx-alert mb-3 flex items-center gap-2", className)} role="alert" {...rest}>
      {children}
    </Alert>
  );
}

// ── Checkbox ────────────────────────────────────────────────────────────────

export interface HxCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children"> {
  /** Text beside the box; omit for a bare box. */
  label?: ReactNode;
}
/** The one checkbox: the template's checkbox (ui/checkbox.tsx, a Radix
 *  `button role="checkbox"`) inside a label, so a click on the words toggles
 *  it and they are its name. `onChange` still gets `e.target.checked` (and
 *  `currentTarget.checked`), built from Radix's `onCheckedChange`; nothing
 *  else on that event is real. The class `hx-checkbox` stays as a marker.
 *  Record: docs/testing/ui-migration/084-hx-checkbox.md. */
export function HxCheckbox({ label, className, style, checked, defaultChecked, disabled, onChange, ...rest }: HxCheckboxProps) {
  return (
    <label
      className={cx("hx-checkbox", disabled && "disabled", className)}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...style }}
    >
      <Checkbox
        {...(rest as ComponentProps<typeof Checkbox>)}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        onCheckedChange={(v) => {
          const target = { checked: v === true };
          onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLInputElement>);
        }}
      />
      {label != null && <span>{label}</span>}
    </label>
  );
}
